/**
 * CopilotAgentPanel — Phase F8 (Agent mode)
 *
 * Natural-language GOAL → POST /ai/copilot/agent/plan → an ordered, human-readable
 * PLAN (op + label + confirm badge on write steps) → "Lancer" executes the plan
 * step-by-step through the existing actuation layer (runPlan in agent-runner),
 * HITL-gating every step where `confirm: true`, with live per-step status.
 *
 * This is the "AI agent that fills forms and takes actions" surface.
 */

import { useCallback, useState, type ComponentType } from 'react'
import {
  Bot,
  Loader2,
  Play,
  RotateCcw,
  CheckCircle2,
  XCircle,
  ShieldAlert,
  Navigation2,
  FileText,
  Search,
  Link2,
  Sparkles,
  BrainCircuit,
  PlusCircle,
  Copy,
  Wand2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { copilotApi, type AgentPlanStep } from '@/services/copilot.service'
import { buildAvailableForms } from '@/lib/copilot/create-registry'
import { runPlanRouted, describeStep, type StepPhase, type GoalRuntime } from '@/lib/copilot/agent-runner'
import { proposeToolsForContext, dispatchTool, type CompanionMode } from '@/lib/copilot/tool-registry'
import type { ToolCard } from '@/lib/copilot/tools/types'
import type { MasProgress } from '@/services/ml-tools.service'
import { useCopilotEntityStore } from '@/stores/useCopilotEntityStore'
import { useCompanionWindowStore } from '@/stores/useCompanionWindowStore'
import { MlResultCard } from '@/components/shared/companion/MlResultCard'

interface CopilotAgentPanelProps {
  moduleContext: string
  pageContext: string
  /** Pre-flight AI-credit guard from the parent sidebar (LlmCreditGuard). */
  ensureSufficientCredit: (needed: number) => Promise<boolean>
}

const OP_ICON: Record<string, ComponentType<{ className?: string }>> = {
  navigate: Navigation2,
  fillForm: FileText,
  search: Search,
  findSimilar: Copy,
  analyze: BrainCircuit,
  suggestLinks: Link2,
  generateRecord: Sparkles,
  createRecord: PlusCircle,
  linkRecords: Link2,
}

const EXAMPLE_GOALS = [
  'Crée un risque cloud puis suggère des contrôles à lier',
  'Rédige une PIA pour un nouveau système de scoring IA',
  'Analyse mes risques critiques et propose un plan d’action',
]

function isWriteStep(step: AgentPlanStep): boolean {
  return (
    step.confirm === true ||
    step.side_effect === 'write' ||
    step.side_effect === 'destructive' ||
    step.side_effect === 'outward'
  )
}

export function CopilotAgentPanel({
  moduleContext,
  pageContext,
  ensureSufficientCredit,
}: CopilotAgentPanelProps) {
  const [goal, setGoal] = useState('')
  const [planning, setPlanning] = useState(false)
  const [plan, setPlan] = useState<AgentPlanStep[] | null>(null)
  const [assistantMessage, setAssistantMessage] = useState('')
  const [grounded, setGrounded] = useState(false)
  const [model, setModel] = useState<string | undefined>(undefined)
  const [running, setRunning] = useState(false)
  const [finished, setFinished] = useState(false)
  const [statuses, setStatuses] = useState<Record<number, StepPhase>>({})
  const [details, setDetails] = useState<Record<number, string>>({})
  const [error, setError] = useState<string | null>(null)
  // Wave 2 — result cards (ML/CV/query), chosen runtime + live MAS progress.
  const [cards, setCards] = useState<{ step: number; card: ToolCard }[]>([])
  const [runtime, setRuntime] = useState<GoalRuntime | null>(null)
  const [masProgress, setMasProgress] = useState<MasProgress | null>(null)

  // Companion autonomy mode (subsystem H) + the viewed detail entity (subsystem
  // B/N) so registry ML/CV tools derive their inputs from the current page.
  const companionMode = useCompanionWindowStore((s) => s.mode) as CompanionMode
  const entity = useCopilotEntityStore((s) => s.entity)

  // Page-relevant ML/CV tool proposals (e.g. "Lancer une prédiction ML sur ce
  // risque" on a risk detail page).
  const proposals = proposeToolsForContext({
    mode: companionMode,
    module: moduleContext,
    pageContext,
    entity,
  })

  const handleProposal = useCallback(
    async (toolName: string) => {
      const creditOk = await ensureSufficientCredit(2)
      if (!creditOk) return
      const res = await dispatchTool(
        toolName,
        {},
        { mode: companionMode, module: moduleContext, pageContext, entity },
      )
      if (res.card) setCards((prev) => [...prev, { step: -1, card: res.card! }])
      if (!res.ok && res.detail) setError(res.detail)
    },
    [ensureSufficientCredit, companionMode, moduleContext, pageContext, entity],
  )

  const handlePlan = useCallback(
    async (goalText: string) => {
      const g = goalText.trim()
      if (!g || planning) return
      setError(null)
      const creditOk = await ensureSufficientCredit(5)
      if (!creditOk) return

      setPlanning(true)
      setPlan(null)
      setStatuses({})
      setDetails({})
      setFinished(false)
      try {
        const res = await copilotApi.agentPlan({
          goal: g,
          module: moduleContext,
          page_context: pageContext,
          available_forms: buildAvailableForms(moduleContext),
          max_steps: 8,
        })
        setAssistantMessage(res.assistant_message)
        setGrounded(res.grounded)
        setModel(res.model)
        setPlan(res.plan)
        if (res.plan.length === 0) {
          setError("Le planificateur n'a proposé aucune étape. Reformulez votre objectif.")
        }
      } catch {
        setError('La planification a échoué. Vérifiez vos crédits IA ou réessayez.')
      } finally {
        setPlanning(false)
      }
    },
    [planning, ensureSufficientCredit, moduleContext, pageContext],
  )

  const handleLaunch = useCallback(async () => {
    if (!plan || plan.length === 0 || running) return
    const creditOk = await ensureSufficientCredit(5)
    if (!creditOk) return

    setRunning(true)
    setFinished(false)
    setStatuses({})
    setDetails({})
    setCards([])
    setRuntime(null)
    setMasProgress(null)

    // runPlanRouted (subsystem D) routes the ALREADY-PLANNED goal without
    // re-planning: a complex/cross-module plan in Agent mode goes to the durable
    // MAS backend (live progress); a simple plan runs in-browser step-by-step
    // (HITL-gated). Registry-resolved steps (ML/CV/query) surface rich cards.
    const result = await runPlanRouted(
      goal.trim() || assistantMessage,
      plan,
      { module: moduleContext, pageContext, mode: companionMode, entity },
      {
        onStep: (stepNumber, phase, detail) => {
          setStatuses((prev) => ({ ...prev, [stepNumber]: phase }))
          if (detail) setDetails((prev) => ({ ...prev, [stepNumber]: detail }))
        },
        onCard: (step, card) => setCards((prev) => [...prev, { step, card }]),
        onRuntime: (rt) => setRuntime(rt),
        onMasProgress: (p) => setMasProgress(p),
      },
    )

    // Mark steps after an in-browser failure as skipped.
    if (result.runtime === 'browser' && result.failedAt !== null) {
      setStatuses((prev) => {
        const next = { ...prev }
        for (const s of plan) {
          if (s.step > result.failedAt! && !next[s.step]) next[s.step] = 'skipped'
        }
        return next
      })
    }

    setRunning(false)
    setFinished(true)
  }, [plan, running, ensureSufficientCredit, goal, assistantMessage, moduleContext, pageContext, companionMode, entity])

  const handleReset = useCallback(() => {
    setGoal('')
    setPlan(null)
    setAssistantMessage('')
    setStatuses({})
    setDetails({})
    setError(null)
    setFinished(false)
    setCards([])
    setRuntime(null)
    setMasProgress(null)
  }, [])

  const doneCount = plan
    ? plan.filter((s) => statuses[s.step] === 'done').length
    : 0

  return (
    <div className="flex flex-1 min-h-0 flex-col" data-copilot-id="copilot-agent-panel">
      {/* Goal input */}
      <div className="shrink-0 border-b p-3 space-y-2">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Bot className="h-3.5 w-3.5 text-primary" />
          Décrivez un objectif — l'agent planifie puis agit.
        </div>
        <Textarea
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handlePlan(goal)
          }}
          placeholder="Ex : Crée un risque cloud puis suggère des contrôles à lier"
          rows={3}
          className="text-sm resize-none"
          data-copilot-id="copilot-agent-goal"
          disabled={planning || running}
        />
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="h-8 gap-1.5 flex-1"
            onClick={() => handlePlan(goal)}
            disabled={!goal.trim() || planning || running}
            data-copilot-id="copilot-agent-plan-btn"
          >
            {planning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            Planifier
          </Button>
          {(plan || assistantMessage) && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 gap-1.5"
              onClick={handleReset}
              disabled={planning || running}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Effacer
            </Button>
          )}
        </div>
        {!plan && !planning && !assistantMessage && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {EXAMPLE_GOALS.map((ex) => (
              <button
                key={ex}
                onClick={() => {
                  setGoal(ex)
                  handlePlan(ex)
                }}
                className="text-[11px] rounded-full border px-2 py-1 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors text-left"
              >
                {ex}
              </button>
            ))}
          </div>
        )}
        {/* Page-relevant ML/CV tool proposals (subsystem N) */}
        {proposals.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 pt-1" data-copilot-id="copilot-ml-proposals">
            <span className="text-[10px] text-muted-foreground">Sur cette page :</span>
            {proposals.map((t) => (
              <button
                key={t.name}
                onClick={() => handleProposal(t.name)}
                disabled={planning || running}
                className="text-[11px] rounded-full border border-primary/30 bg-primary/5 px-2 py-1 text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
                data-copilot-id={`copilot-ml-proposal-${t.name}`}
              >
                {t.description}
              </button>
            ))}
          </div>
        )}
        {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      </div>

      {/* Plan + execution */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-3 space-y-3">
          {assistantMessage && (
            <div className="rounded-lg border bg-muted/40 p-3 text-sm">
              <div className="flex items-start gap-2">
                <Bot className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <p className="whitespace-pre-wrap">{assistantMessage}</p>
              </div>
              <div className="mt-2 flex items-center gap-1.5">
                {grounded && (
                  <Badge variant="secondary" className="text-[10px] h-4 gap-1">
                    <BrainCircuit className="h-2.5 w-2.5" /> Fondé RAG
                  </Badge>
                )}
                {model && (
                  <Badge variant="outline" className="text-[10px] h-4">
                    {model}
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Chosen runtime badge (subsystem D) */}
          {runtime && (
            <div className="flex items-center gap-1.5" data-copilot-id="copilot-agent-runtime">
              <span className="text-[11px] text-muted-foreground">Exécution :</span>
              <Badge
                variant={runtime === 'mas' ? 'default' : 'secondary'}
                className="text-[10px] h-4"
                data-testid="agent-runtime"
              >
                {runtime === 'mas' ? 'Multi-agents (MAS)' : 'Local (rapide)'}
              </Badge>
            </div>
          )}

          {/* Live MAS plan progress (subsystem D) */}
          {masProgress && (
            <div
              className="rounded-lg border bg-muted/40 p-3 space-y-2"
              data-copilot-id="copilot-mas-progress"
              data-testid="mas-progress"
            >
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold">Plan MAS — {masProgress.status}</span>
                <span className="tabular-nums text-muted-foreground">{masProgress.progressPct}%</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${Math.min(100, Math.max(0, masProgress.progressPct))}%` }}
                />
              </div>
              <div className="space-y-1">
                {masProgress.subtasks.slice(0, 8).map((st) => (
                  <div key={st.key} className="flex items-center justify-between gap-2 text-[11px]">
                    <span className="truncate">{st.title}</span>
                    <Badge variant="outline" className="text-[9px] h-4 px-1 shrink-0">
                      {st.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Result cards (ML/CV/query) — subsystem N */}
          {cards.length > 0 && (
            <div className="space-y-2" data-copilot-id="copilot-result-cards">
              {cards.map((c, i) => (
                <MlResultCard key={`${c.step}-${i}`} card={c.card} />
              ))}
            </div>
          )}

          {plan && plan.length > 0 && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-muted-foreground">
                  Plan — {plan.length} étape(s)
                </p>
                {(running || finished) && (
                  <span className="text-[11px] text-muted-foreground">
                    {doneCount}/{plan.length} terminée(s)
                  </span>
                )}
              </div>

              {[...plan]
                .sort((a, b) => a.step - b.step)
                .map((step) => {
                  const Icon = OP_ICON[step.op] ?? Sparkles
                  const status = statuses[step.step]
                  const detail = details[step.step]
                  const write = isWriteStep(step)
                  return (
                    <div
                      key={step.step}
                      className={cn(
                        'rounded-md border px-2.5 py-2',
                        status === 'running' && 'border-primary/60 bg-primary/5',
                        status === 'done' && 'border-emerald-300/60 bg-emerald-50/40 dark:bg-emerald-950/20',
                        status === 'failed' && 'border-red-300/60 bg-red-50/40 dark:bg-red-950/20',
                        status === 'skipped' && 'opacity-50',
                        !status && 'border-border',
                      )}
                      data-copilot-id={`copilot-agent-step-${step.step}`}
                    >
                      <div className="flex items-start gap-2">
                        {/* Status / number */}
                        <div className="shrink-0 mt-0.5">
                          {status === 'running' && <Loader2 className="h-4 w-4 text-primary animate-spin" />}
                          {status === 'done' && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                          {status === 'failed' && <XCircle className="h-4 w-4 text-red-600" />}
                          {(!status || status === 'skipped') && (
                            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground">
                              {step.step}
                            </span>
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <span className="text-xs font-medium">{describeStep(step)}</span>
                            <Badge variant="outline" className="text-[9px] h-4 px-1 font-mono">
                              {step.op}
                            </Badge>
                            {write && (
                              <Badge className="text-[9px] h-4 px-1 gap-0.5 bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border-amber-300/50">
                                <ShieldAlert className="h-2.5 w-2.5" />
                                confirmation
                              </Badge>
                            )}
                          </div>
                          {detail && (
                            <p
                              className={cn(
                                'text-[11px] mt-1',
                                status === 'failed' ? 'text-red-600' : 'text-muted-foreground',
                              )}
                            >
                              {detail}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}

              {/* Launch */}
              <div className="pt-2 flex items-center gap-2">
                <Button
                  size="sm"
                  className="h-8 gap-1.5 flex-1"
                  onClick={handleLaunch}
                  disabled={running || finished}
                  data-copilot-id="copilot-agent-launch-btn"
                >
                  {running ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Exécution…
                    </>
                  ) : finished ? (
                    <>
                      <CheckCircle2 className="h-4 w-4" /> Terminé
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" /> Lancer
                    </>
                  )}
                </Button>
              </div>
              {finished && (
                <p className="text-[11px] text-muted-foreground pt-1">
                  Processus terminé — {doneCount}/{plan.length} étape(s) réussie(s). Chaque écriture a
                  été confirmée par vous.
                </p>
              )}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

export default CopilotAgentPanel
